import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { normalizeUsPhoneNumber } from "@/modules/phoneVerification/normalizeUsPhoneNumber";

// Importing the customParseFormat plugin to enable parsing of custom date formats
// This is necessary for the isValidDateFormat function to work correctly
dayjs.extend(customParseFormat);

export const isRequiredValid = (value: string | boolean | number | null | undefined) => {
  return !(value == null || (typeof value === "string" && value.trim() === ""));
};

export const isValidEmail = (email: string) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

export const isValidUsPhoneNumber = (phoneNumber: string): boolean =>
  /^\+1\d{10}$/.test(normalizeUsPhoneNumber(phoneNumber));

// Anchored, case-insensitive canonical UUID shape. Security-relevant wherever an untrusted string
// is substituted into an authenticated request path (e.g. the Beautifi return page's
// transaction_id): anything that is not a UUID must be rejected before it reaches a path template.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUuid = (value: string): boolean => UUID_PATTERN.test(value);

const UNSAFE_URL_SCHEMES = new Set(["javascript:", "data:", "vbscript:", "file:", "blob:"]);

export const isUnsafeUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) return false;

  if (trimmed.startsWith("//")) return true;

  const colonIndex = trimmed.indexOf(":");
  if (colonIndex <= 0) return false;

  const prefix = trimmed.slice(0, colonIndex);
  if (!/^[a-z][a-z0-9+.-]*$/i.test(prefix)) return false;

  return UNSAFE_URL_SCHEMES.has(`${prefix.toLowerCase()}:`);
};

export const isValidDateFormat = (date: string, format: dayjs.OptionType = "L"): boolean => {
  let isValid = false;

  const resolvedFormat = format === "L" ? dayjs.localeData().longDateFormat("L") : format;

  const parsed = dayjs(date, resolvedFormat, true);

  if (parsed.isValid()) {
    const year = parsed.year();
    const minYear = 1900;
    const today = dayjs();
    const inputDate = dayjs(date, resolvedFormat);

    isValid = year >= minYear && inputDate.isBefore(today);
  }
  return isValid;
};
